import { useContext, useEffect, useRef, useState } from 'react';
import JobsContext from '../../contexts/data/JobsContext';
import ChatContext from '../../contexts/data/ChatContext';
import { useWindowSize } from '@leedpulse-monorepo/hooks';
import { Table, Button, Popconfirm, Tag } from 'antd';
import { JobType, jobTypeAsString } from "@leedpulse-monorepo/enums";
import { getInterestLevel } from '@leedpulse-monorepo/utils';
import { ContextStatus } from "@leedpulse-monorepo/enums";
import JobWithContact from '../../../domain/models/@aggregates/JobWithContact';
import { CONTACTS_PAGE_PATH } from '../../pages/contacts/ContactsPage';
import moment from 'moment';
import Job from '../../../domain/models/Job';
import { DeleteOutlined } from '@ant-design/icons';
import { advancedOnRow, getTagColor } from '../../../core/utils/commonUtils';
import { useNavigate } from 'react-router-dom';


interface JobsListProps {
  scopedSubscriptionId: string | null;
  scopedContactId: string | null;
}

const JobsList: React.FC<JobsListProps> = ({
  scopedSubscriptionId,
  scopedContactId,
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [currentData, setCurrentData] = useState([] as JobWithContact[]);
  const jobsContext = useContext(JobsContext);
  const chatContext = useContext(ChatContext);
  const windowSize = useWindowSize();
  const fetchNextCalled = useRef(false);

  const navigate = useNavigate();

  const handlePaginationChange = (page: number) => {
    setCurrentPage(page);
    const currentSlice = jobsContext.items.slice(
      (page - 1) * jobsContext.hitsPerPage,
      page * jobsContext.hitsPerPage
    );
    if (currentSlice.length === 0) {
      fetchNextCalled.current = true;
      jobsContext.fetchNext();
    }
  };

  const handlePageSizeChange = (_current: number, newPageSize: number) => {
    setCurrentPage(1);
    jobsContext.fetch(scopedSubscriptionId, scopedContactId, newPageSize);
  };

  const handleDeleteJob = async (jobId: Job) => {
    if (jobId) {
      await chatContext.handleDeleteJob(jobId);
    }
  };

  // This hook is used to initialize the jobs list
  useEffect(() => {
    if (
      jobsContext.status === ContextStatus.IDLE ||
      jobsContext.scopedSubscriptionId !== scopedSubscriptionId ||
      jobsContext.scopedContactId !== scopedContactId
    ) {
      jobsContext.fetch(scopedSubscriptionId, scopedContactId);
    }
    return () => {
      jobsContext.reset();
    };
  }, []);

  // This hook is triggered when a 'page next' is called to get the new slice of items
  useEffect(() => {
    if (jobsContext.status === ContextStatus.SUCCESS) {
      const currentSlice = jobsContext.items.slice(
        (currentPage - 1) * jobsContext.hitsPerPage,
        currentPage * jobsContext.hitsPerPage
      );
      // Synchro avec un contexte qui fetche : l'etat local suit le resultat d'un
      // chargement declenche ici. Architecture Context + use case du repo.
      // eslint-disable-next-line react/set-state-in-effect
      setCurrentData(currentSlice);
      fetchNextCalled.current = false;
    }
  }, [jobsContext.status]);

  // This hook is triggered when the current page changes to update the displayed data
  useEffect(() => {
    if (!fetchNextCalled.current) {
      const currentSlice = jobsContext.items.slice(
        (currentPage - 1) * jobsContext.hitsPerPage,
        currentPage * jobsContext.hitsPerPage
      );
      setCurrentData(currentSlice);
    }
  }, [currentPage]);

  // This hook is used to go back to first page when refreshing the list
  // Retour a la page 1 quand la tranche courante est vide. Calcule pendant le
  // rendu (pattern « ajuster l'etat quand une prop change » de la doc React) :
  // dans un effet, chaque fin de chargement coutait un second rendu du tableau.
  const [lastStatusForClamp, setLastStatusForClamp] = useState(jobsContext.status);
  if (jobsContext.status !== lastStatusForClamp) {
    setLastStatusForClamp(jobsContext.status);
    if (
      jobsContext.status === ContextStatus.SUCCESS &&
      jobsContext.items.slice(
        (currentPage - 1) * jobsContext.hitsPerPage,
        currentPage * jobsContext.hitsPerPage
      ).length === 0
    ) {
      setCurrentPage(1);
    }
  }

  return (
    <Table
      pagination={{
        current: currentPage,
        total: jobsContext.count,
        pageSize: jobsContext.hitsPerPage,
        onChange: handlePaginationChange,
        onShowSizeChange: (_current, newSize) => handlePageSizeChange(_current, newSize),
        showSizeChanger: true,
        simple: true,
        pageSizeOptions: [20, 50, 100, 200],
        position: ['bottomRight', 'topRight'],
        disabled: jobsContext.status === ContextStatus.LOADING,
      }}
      loading={jobsContext.status === ContextStatus.LOADING}
      columns={
        windowSize.width > 576
          ? [
            {
              title: 'Nom complet',
              dataIndex: 'contactFullname',
              key: 'contactFullname',
              render: (contactFullname: string, record) => (
                <div>
                  <div>{contactFullname}</div>
                  <Tag className='contact-tile__content__owner-tag' color={getTagColor(record?.contact?.qualification_ai?.qualification || "not_available")}>
                    &#8226; {getInterestLevel(record?.contact?.qualification_ai?.qualification || "not_available")}
                  </Tag>
                </div>
              ),
            },
            {
              title: 'Type',
              dataIndex: 'type',
              key: 'type',
              render: (type: JobType) => jobTypeAsString(type),
            },
            {
              title: 'Date',
              dataIndex: 'timestamp',
              key: 'timestamp',
              render: (timestamp: Date) => moment(timestamp).fromNow(),
            },
            {
              title: 'Action',
              key: 'actions',
              render: (_, record) => (
                <Popconfirm
                  title="Êtes-vous sûr de vouloir continuer ?"
                  onConfirm={(event) => {
                    if (event) {
                      event.stopPropagation();
                      handleDeleteJob(record.job);
                    }
                  }}
                  onCancel={(event) => {
                    if (event) {

                      event.stopPropagation();
                    }
                  }}
                  okText="Oui"
                  cancelText="Non"
                >
                  <Button type="link" danger className="delete-button" style={{ paddingLeft: "0" }}>
                    <DeleteOutlined style={{ fontSize: 20 }} />
                  </Button>
                </Popconfirm>

              )
            },
          ]
          : [
            {
              title: 'Nom complet',
              dataIndex: 'contactFullname',
              key: 'contactFullname',
            },
            {
              title: 'Type',
              dataIndex: 'type',
              key: 'type',
              render: (type: JobType) => jobTypeAsString(type),
            },
            {
              title: 'Date',
              dataIndex: 'timestamp',
              key: 'timestamp',
              render: (timestamp: Date) => moment(timestamp).fromNow(),
            },
            {
              title: 'Action',
              key: 'actions',
              render: (_, record) => (
                <Popconfirm
                  title="Êtes-vous sûr de vouloir continuer ?"
                  onConfirm={(event) => {
                    if (event) {
                      event.stopPropagation();
                      handleDeleteJob(record.job);
                    }
                  }}
                  onCancel={(event) => {
                    if (event) {
                      event.stopPropagation();
                    }
                  }}
                  okText="Oui"
                  cancelText="Non"
                >
                  <Button className="delete-button" type="link" danger style={{ paddingLeft: "0" }}>
                    <DeleteOutlined style={{ fontSize: 20 }} />
                  </Button>
                </Popconfirm>
              ),
            },
          ]
      }
      rowKey={(record) => record.job.id}
      dataSource={currentData}
      onRow={(record) =>
        advancedOnRow({
          url: `${CONTACTS_PAGE_PATH}/${record.contact.id}`,
          navigate,
          onClickBefore: (event) => {
            const target = event?.target as HTMLElement;
            if (target?.closest('.delete-button')) return false;
          },
          onMouseDownBefore: (event) => {
            const target = event?.target as HTMLElement;
            if (target?.closest('.delete-button')) return false;
          },
          className: 'table-row',
        })
      }
    />
  );
};

export default JobsList;
